// core/services/searchService.ts — 1.24.00 real search over `GET /api/search/`.
//
// Store-less: results are per-query and owned by the caller (see
// hooks/useSearch.ts, which adds the debounce + in-flight sequence guard).
// Components must NOT import this directly — use useSearch.
//
// The server trims `q` and rejects anything under 2 or over 80 characters with
// 400 {code:'validation'}; the hook only calls this at >= 2 characters, and the
// 80 cap is enforced here so a long paste never becomes a guaranteed 400.

import type { SearchResults, SearchType } from '@/core/models/search';
import { api } from '@/core/lib/apiClient';

export const SEARCH_MAX_QUERY_LEN = 80;
export const SEARCH_DEFAULT_LIMIT = 20;

export interface SearchOptions {
  type?: SearchType;
  /** 1..20; the server clamps, so this only shapes the request. */
  limit?: number;
  /** Encoded, allowlisted 1.26 search dimensions. */
  filters?: string;
}

export const searchService = {
  /**
   * `GET /search/?q=…&type=…&limit=…`. Every list in the envelope is at most
   * `limit` long. Throws ApiError on 400/429 so the hook can show a real error
   * state rather than a hollow "nothing matched".
   */
  async search(q: string, opts: SearchOptions = {}): Promise<SearchResults> {
    const trimmed = q.trim().slice(0, SEARCH_MAX_QUERY_LEN);
    const params = new URLSearchParams();
    params.set('q', trimmed);
    params.set('type', opts.type ?? 'all');
    params.set('limit', String(opts.limit ?? SEARCH_DEFAULT_LIMIT));
    const filters = new URLSearchParams(opts.filters);
    for (const key of ['author', 'community', 'topic', 'from', 'until', 'media']) {
      const value = filters.get(key);
      if (value && value !== 'any') params.set(key, value);
    }
    return api<SearchResults>(`/search/?${params.toString()}`);
  },
} as const;
